import logoSource from '/static/assets/images/logo.png';
import {Features} from 'constants/plan-access/features';
import {Field, FieldProps, Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import Button from 'ui/@_components/button';
import Dialog from 'ui/@_components/kit/dialog';
import FormControl from 'ui/@_components/kit/form/form-control';
import Label from 'ui/@_components/kit/form/label';
import Textarea from 'ui/@_components/kit/form/textarea';
import ActionsStepsPreviewUrl from '/static/kit-images/preview/action-steps-preview.png';
import AssessmentPollsPreviewUrl from '/static/kit-images/preview/assessment-polls.png';
import CandidateCalibrationPreviewUrl from '/static/kit-images/preview/candidate-calibration-preview.png';
import InstillFlowPreviewUrl from '/static/kit-images/preview/instill-flow-preview.png';
import {useSafeCurrentCompanyUuid} from 'ui/@atoms/current-company';
import {useFeatureRequest} from 'ui/@hooks/mutations/use-feature-request';
import * as Yup from 'yup';
import styles from './styles.module.scss';
import apiErrorGuard from 'utils/error-utils/api-error-guard';

interface requestFormValues {
  message: string;
}

const initialRequestValues: requestFormValues = {
  message: '',
};

const requestFormValidationSchema = Yup.object().shape({
  message: Yup.string().required('Message is required'),
});

interface ElementProps {
  isOpen: boolean;
  onHide: () => void;
  feature: Features;
}

const PagePlanAccessModal = ({isOpen, onHide, feature}: ElementProps) => {
  const {t} = useTranslation('moduleComponents', {
    keyPrefix: 'modals.page-plan-access-modal',
  });

  const requestFeature = useFeatureRequest();
  const companyUuid = useSafeCurrentCompanyUuid();

  const onSubmit = () => {
    requestFeature.mutate(
      {
        companyUuid,
        featureName: feature,
      },
      {
        onSuccess: () => {
          toast.success(t('success'));
        },
        onError: apiErrorGuard(() => {
          toast.error(t('failure'));
        }),
      }
    );

    onHide();
  };

  const mapFeatureDetails: Partial<
    Record<Features, {imgSrc: string; title: string; subtitle: string}>
  > = {
    [Features.ASSESSMENTS_POLLS]: {
      imgSrc: AssessmentPollsPreviewUrl,
      title: t('assessment-polls.title'),
      subtitle: t('assessment-polls.subtitle'),
    },
    [Features.CANDIDATE_CALIBRATION]: {
      imgSrc: CandidateCalibrationPreviewUrl,
      title: t('candidate-calibration.title'),
      subtitle: t('candidate-calibration.subtitle'),
    },
    [Features.ACTION_STEPS]: {
      imgSrc: ActionsStepsPreviewUrl,
      title: t('action-steps.title'),
      subtitle: t('action-steps.subtitle'),
    },
    [Features.INSTILL_FLOW]: {
      imgSrc: InstillFlowPreviewUrl,
      title: t('instill-flow.title'),
      subtitle: t('instill-flow.subtitle'),
    },
  };

  const renderBottomContent = () => {
    // Plans and Pricing is hidden (IT-2195), so plan admins get the same
    // request-access form as everyone else instead of an upgrade link.
    return (
      <>
        <Formik
          initialValues={initialRequestValues}
          validationSchema={requestFormValidationSchema}
          onSubmit={onSubmit}
          validateOnMount
        >
          {({errors, touched}) => (
            <Form className={styles.accessForm}>
              <Field name="message">
                {({field}: FieldProps<string>) => (
                  <FormControl
                    touched={!!touched.message}
                    unknownTypeError={errors.message}
                  >
                    <div>
                      <Label isRequired>{t('form-label')}</Label>

                      <Textarea
                        {...field}
                        rows={5}
                        placeholder={t('start-typing')}
                      />
                    </div>
                  </FormControl>
                )}
              </Field>

              <div className={styles.footerContainer}>
                <Button variant="secondary" onClick={onHide}>
                  {t('no-thanks')}
                </Button>

                <Button type="submit">{t('contact-admin')}</Button>
              </div>
            </Form>
          )}
        </Formik>
      </>
    );
  };

  return (
    <Dialog isOpen={isOpen} onClose={onHide}>
      <div className={styles.content}>
        <img
          src={logoSource}
          alt="Instill Logo"
          role="presentation"
          className={styles.logo}
          height="44px"
          width="65px"
        />

        <div className={styles.textContent}>
          <h1 className={styles.title}>{mapFeatureDetails[feature]?.title}</h1>

          <h2 className={styles.subtitle}>
            {mapFeatureDetails[feature]?.subtitle}
          </h2>
        </div>

        <div className={styles.imageContainer}>
          <img
            className={styles.previewImage}
            src={mapFeatureDetails[feature]?.imgSrc}
            alt={mapFeatureDetails[feature]?.title}
            width="424px"
            height="317px"
          />
        </div>

        {renderBottomContent()}
      </div>
    </Dialog>
  );
};

export default PagePlanAccessModal;
